import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(sessionStorage.getItem('token'));
  const skipAutoFetchRef = React.useRef(false);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      if (!skipAutoFetchRef.current) {
        fetchCurrentUser();
      } else {
        skipAutoFetchRef.current = false;
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    // Trim to eliminate accidental leading/trailing whitespace from copy-paste of
    // credentials (a common cause of "Invalid credentials" for freshly-onboarded users).
    const cleanUsername = (username || '').trim();
    const cleanPassword = (password || '').trim();
    const response = await axios.post(`${API}/auth/login`, {
      username: cleanUsername,
      password: cleanPassword,
    });
    const { access_token, user: userData, must_change_password } = response.data;
    
    sessionStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    
    if (must_change_password) {
      skipAutoFetchRef.current = true;
      setToken(access_token);
      return { ...userData, must_change_password: true };
    }
    
    setToken(access_token);
    setUser(userData);
    return { ...userData, must_change_password: false };
  };

  const completeLogin = async () => {
    await fetchCurrentUser();
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { access_token, user: newUser } = response.data;
    
    sessionStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(newUser);
    
    return newUser;
  };

  const logout = () => {
    sessionStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, completeLogin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};